import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * If a removed item is part of a free-gift bundle, all linked items are removed together.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.#removeLinkedLineItems(line);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    cartTotal?.shimmer();

    this.#requestCartChange(line, quantity)
      .then((parsedResponseText) => {
        this.#handleParsedCartResponse(line, parsedResponseText);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Removes main product and linked free gifts together.
   * Works both ways:
   * - Removing main product removes gift products.
   * - Removing gift product removes the linked main product.
   * @param {number} line - The cart line number.
   */
  #removeLinkedLineItems(line) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:linked-gift-user-action');

    this.#disableCartItems();

    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    this.#fetchCart()
      .then((cart) => {
        const targetItem = cart?.items?.[line - 1];

        if (!targetItem || !targetItem.key) {
          return this.#requestCartChange(line, 0).then((parsedResponseText) => {
            return {
              parsedResponseText,
              keysToRemove: [],
            };
          });
        }

        const keysToRemove = this.#getLinkedRemovalKeys(cart, targetItem);

        if (!keysToRemove.length) {
          keysToRemove.push(targetItem.key);
        }

        this.#optimisticallyRemoveRowsByKeys(keysToRemove, line);

        return this.#requestCartUpdateByKeys(keysToRemove).then((parsedResponseText) => {
          return {
            parsedResponseText,
            keysToRemove,
          };
        });
      })
      .then(({ parsedResponseText }) => {
        this.#handleParsedCartResponse(line, parsedResponseText);
      })
      .catch((error) => {
        console.error('Linked cart remove error:', error);

        const parsedError = error && typeof error === 'object' ? error : { errors: 'Something went wrong. Please try again.' };
        this.#handleCartError(line, parsedError);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Requests Shopify cart/change.js.
   * @param {number} line
   * @param {number} quantity
   * @returns {Promise<Object>}
   */
  #requestCartChange(line, quantity) {
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.#getSectionsToUpdate().join(','),
      sections_url: window.location.pathname,
    });

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => JSON.parse(responseText));
  }

  /**
   * Requests Shopify cart/update.js using line item keys.
   * @param {string[]} keysToRemove
   * @returns {Promise<Object>}
   */
  #requestCartUpdateByKeys(keysToRemove) {
    const updates = {};

    keysToRemove.forEach((key) => {
      if (key) updates[key] = 0;
    });

    const body = JSON.stringify({
      updates,
      sections: this.#getSectionsToUpdate().join(','),
      sections_url: window.location.pathname,
    });

    return fetch(this.#getCartUpdateUrl(), fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => JSON.parse(responseText));
  }

  /**
   * Fetches current Shopify cart.
   * @returns {Promise<Object>}
   */
  #fetchCart() {
    return fetch(this.#getCartJsonUrl(), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    }).then((response) => response.json());
  }

  /**
   * Handles successful cart response and refreshes sections.
   * @param {number} line
   * @param {Object} parsedResponseText
   */
  #handleParsedCartResponse(line, parsedResponseText) {
    resetShimmer(this);

    if (parsedResponseText.errors) {
      this.#handleCartError(line, parsedResponseText);
      return;
    }

    if (!parsedResponseText.sections || !parsedResponseText.sections[this.sectionId]) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    const newSectionHTML = new DOMParser().parseFromString(
      parsedResponseText.sections[this.sectionId],
      'text/html'
    );

    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.#updateQuantitySelectors(parsedResponseText);

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponseText, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );

    morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
      mode: this.isDrawer ? 'hydration' : 'full',
    });

    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Finds all linked item keys to remove.
   * Best method: shared hidden property "_Bundle ID".
   * Fallback method: detects "_Free Gift" when there is only one main product in cart.
   * @param {Object} cart
   * @param {Object} targetItem
   * @returns {string[]}
   */
  #getLinkedRemovalKeys(cart, targetItem) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const targetKey = String(targetItem.key || '');
    const targetBundleId = this.#getBundleId(targetItem);

    if (targetBundleId) {
      return this.#uniqueKeys(
        items
          .filter((item) => this.#getBundleId(item) === targetBundleId)
          .map((item) => item.key)
      );
    }

    const targetIsGift = this.#isFreeGiftItem(targetItem);
    const targetLinkedMainVariantId = this.#getProperty(targetItem, [
      '_Linked Main Variant ID',
      '_Main Variant ID',
      '_Parent Variant ID',
    ]);

    const targetLinkedMainKey = this.#getProperty(targetItem, [
      '_Linked Main Key',
      '_Main Product Key',
      '_Parent Key',
    ]);

    if (targetIsGift) {
      const linkedKeys = [targetKey];

      if (targetLinkedMainKey) {
        items.forEach((item) => {
          const itemKey = String(item.key || '');

          if (itemKey === String(targetLinkedMainKey)) {
            linkedKeys.push(itemKey);
          }

          const itemLinkedMainKey = this.#getProperty(item, [
            '_Linked Main Key',
            '_Main Product Key',
            '_Parent Key',
          ]);

          if (itemLinkedMainKey && String(itemLinkedMainKey) === String(targetLinkedMainKey)) {
            linkedKeys.push(itemKey);
          }
        });

        return this.#uniqueKeys(linkedKeys);
      }

      if (targetLinkedMainVariantId) {
        items.forEach((item) => {
          const itemKey = String(item.key || '');

          if (String(item.variant_id) === String(targetLinkedMainVariantId)) {
            linkedKeys.push(itemKey);
          }

          const itemLinkedMainVariantId = this.#getProperty(item, [
            '_Linked Main Variant ID',
            '_Main Variant ID',
            '_Parent Variant ID',
          ]);

          if (itemLinkedMainVariantId && String(itemLinkedMainVariantId) === String(targetLinkedMainVariantId)) {
            linkedKeys.push(itemKey);
          }
        });

        return this.#uniqueKeys(linkedKeys);
      }

      return this.#legacyFallbackRemovalKeys(items, targetItem);
    }

    const linkedGiftKeys = items
      .filter((item) => {
        if (!this.#isFreeGiftItem(item)) return false;

        const giftLinkedMainVariantId = this.#getProperty(item, [
          '_Linked Main Variant ID',
          '_Main Variant ID',
          '_Parent Variant ID',
        ]);

        const giftLinkedMainKey = this.#getProperty(item, [
          '_Linked Main Key',
          '_Main Product Key',
          '_Parent Key',
        ]);

        if (giftLinkedMainVariantId && String(giftLinkedMainVariantId) === String(targetItem.variant_id)) {
          return true;
        }

        if (giftLinkedMainKey && String(giftLinkedMainKey) === targetKey) {
          return true;
        }

        return false;
      })
      .map((item) => item.key);

    if (linkedGiftKeys.length) {
      return this.#uniqueKeys([targetKey, ...linkedGiftKeys]);
    }

    return this.#legacyFallbackRemovalKeys(items, targetItem);
  }

  /**
   * Conservative fallback for carts without bundle IDs.
   * If cart has only one non-gift main product, removing either the main or a gift removes the whole bundle.
   * @param {Object[]} items
   * @param {Object} targetItem
   * @returns {string[]}
   */
  #legacyFallbackRemovalKeys(items, targetItem) {
    const targetKey = String(targetItem.key || '');
    const giftItems = items.filter((item) => this.#isFreeGiftItem(item));
    const nonGiftItems = items.filter((item) => !this.#isFreeGiftItem(item));

    if (!giftItems.length) {
      return [targetKey];
    }

    if (nonGiftItems.length === 1) {
      return this.#uniqueKeys([
        nonGiftItems[0].key,
        ...giftItems.map((item) => item.key),
      ]);
    }

    if (!this.#isFreeGiftItem(targetItem)) {
      return [targetKey];
    }

    return [targetKey];
  }

  /**
   * Gets hidden bundle id from line item properties.
   * @param {Object} item
   * @returns {string}
   */
  #getBundleId(item) {
    return this.#getProperty(item, [
      '_Bundle ID',
      '_Gift Bundle ID',
      '_Linked Bundle ID',
      '_BundleID',
    ]);
  }

  /**
   * Detects free gift items using hidden properties.
   * @param {Object} item
   * @returns {boolean}
   */
  #isFreeGiftItem(item) {
    const freeGiftValue = this.#getProperty(item, [
      '_Free Gift',
      '_free_gift',
      'Free Gift',
    ]);

    const bundleRole = this.#getProperty(item, [
      '_Bundle Role',
      '_Gift Role',
    ]);

    const giftSource = this.#getProperty(item, [
      '_Gift Source',
      '_Bonus Title',
      '_Gift Product',
    ]);

    const normalizedFreeGiftValue = String(freeGiftValue || '').toLowerCase().trim();
    const normalizedBundleRole = String(bundleRole || '').toLowerCase().trim();

    if (normalizedFreeGiftValue === 'yes') return true;
    if (normalizedFreeGiftValue === 'true') return true;
    if (normalizedFreeGiftValue === '1') return true;
    if (normalizedBundleRole === 'free gift') return true;
    if (giftSource) return true;

    return false;
  }

  /**
   * Gets a property value from Shopify line item properties.
   * @param {Object} item
   * @param {string[]} names
   * @returns {string}
   */
  #getProperty(item, names) {
    const properties = item?.properties || {};

    for (const name of names) {
      if (Object.prototype.hasOwnProperty.call(properties, name)) {
        const value = properties[name];

        if (value === null || value === undefined) return '';

        return String(value).trim();
      }
    }

    return '';
  }

  /**
   * Returns unique valid keys.
   * @param {Array<string | number>} keys
   * @returns {string[]}
   */
  #uniqueKeys(keys) {
    return Array.from(
      new Set(
        keys
          .filter(Boolean)
          .map((key) => String(key))
      )
    );
  }

  /**
   * Optimistically removes matching cart rows from the DOM.
   * @param {string[]} keysToRemove
   * @param {number} fallbackLine
   */
  #optimisticallyRemoveRowsByKeys(keysToRemove, fallbackLine) {
    const keySet = new Set(keysToRemove.map((key) => String(key)));

    let rowsToRemove = this.refs.cartItemRows.filter((row) => {
      const rowKey = row.dataset.key ? String(row.dataset.key) : '';
      const parentKey = row.dataset.parentKey ? String(row.dataset.parentKey) : '';

      return keySet.has(rowKey) || keySet.has(parentKey);
    });

    if (!rowsToRemove.length) {
      const fallbackRow = this.refs.cartItemRows[fallbackLine - 1];

      if (fallbackRow) {
        rowsToRemove = [
          fallbackRow,
          ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === fallbackRow.dataset.key),
        ];
      }
    }

    rowsToRemove = Array.from(new Set(rowsToRemove));

    if (!rowsToRemove.length) return;

    const isEmptyCart = rowsToRemove.length === this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  /**
   * Gets section ids that need to update.
   * @returns {string[]}
   */
  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return Array.from(sectionsToUpdate);
  }

  /**
   * Builds Shopify localized route.
   * @param {string} path
   * @returns {string}
   */
  #buildShopifyRoute(path) {
    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    return `${root.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  }

  /**
   * Gets cart.js URL.
   * @returns {string}
   */
  #getCartJsonUrl() {
    return this.#buildShopifyRoute('cart.js');
  }

  /**
   * Gets cart/update.js URL.
   * @returns {string}
   */
  #getCartUpdateUrl() {
    if (window.Theme && Theme.routes && Theme.routes.cart_update_url) {
      return Theme.routes.cart_update_url;
    }

    return this.#buildShopifyRoute('cart/update.js');
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors || 'Something went wrong. Please try again.';
    }

    if (cartItemErrorContainer instanceof HTMLElement) {
      cartItemErrorContainer.classList.remove('hidden');
    }
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}